/*
 * @Author: linguang linguang@nextmar.com
 * @Date: 2025-01-22 18:49:32
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2025-10-29 14:51:52
 * @FilePath: /src/components/Layout/HeaderV2/index.tsx
 * @Description:
 */
'use client';
import { getHomePageData } from '@/api/home';
import { getSearchDataByType, getSearchKeywords } from '@/api/public';
import type { DingTongConfig } from '@/api/types/ding-tong';
import { ArrayElement, GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { checkHeaderConfig } from '@/utils/tools';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchStore } from './components/SearchComponent/store/search-store';
import HeaderMobile from './Header.m';
import HeaderPC from './Header.pc';

export interface HeaderProps {
  logoData: ArrayElement<GetResponseType<ApiResponseDefinition['POST:/api/block/block']>['list']>;
  dtInfo: DingTongConfig | null;
  quickEntryData: ArrayElement<
    GetResponseType<ApiResponseDefinition['POST:/api/block/block']>['list']
  >;
  noticeData: ArrayElement<GetResponseType<ApiResponseDefinition['POST:/api/block/block']>['list']>;
  headerNavList?: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
  deviceType: EPlatform;
  isBot?: boolean;
}

const ZeeloolHeader = (props: HeaderProps) => {
  const canBuyCartsList = useStore((state) => state.canBuyCartsList);
  const [cartProductNum, setCartProductNum] = useState<string | number>(0);
  const setIsShowHeaderNav = useStore((state) => state.setIsShowHeaderNav);
  const setIsHeaderDarkMode = useStore((state) => state.setIsHeaderDarkMode);
  const isShowDtBannerByPage = useStore((state) => state.isShowDtBannerByPage);
  const setIsShowDtBannerByPage = useStore((state) => state.setIsShowDtBannerByPage);
  const isShowHeader = useStore((state) => state.isShowHeaderNav);
  const isDarkMode = useStore((state) => state.isHeaderDarkMode);
  const { pathname } = useRouteMetadata();
  const isShowAppDownloadBanner = useStore((state) => state.isShowAppDownloadBanner);
  const setIsShowAppDownloadBanner = useStore((state) => state.setIsShowAppDownloadBanner);
  const isMobileDevice = props.deviceType !== EPlatform.WEB;
  const isClientInit = useRef(false);

  const headerConfig = useMemo(() => {
    const c = checkHeaderConfig(pathname, isMobileDevice);
    if (props.deviceType === EPlatform.IOS || props.deviceType === EPlatform.ANDROID) {
      return { ...c, isShowHeaderNav: false };
    }
    return c;
  }, [pathname, isMobileDevice, props.deviceType]);

  // 搜索相关的store
  const setSearchData = useSearchStore((state) => state.setSearchData);
  const setKeywordsMaps = useSearchStore((state) => state.setKeywordsMaps);
  const setHotProductList = useSearchStore((state) => state.setHotProductList);
  const setSearchHistory = useSearchStore((state) => state.setSearchHistory);
  const isSearchDataLoaded = useSearchStore((state) => state.isSearchDataLoaded);
  const isSearchDataLoading = useSearchStore((state) => state.isSearchDataLoading);
  const setIsSearchDataLoaded = useSearchStore((state) => state.setIsSearchDataLoaded);
  const setIsSearchDataLoading = useSearchStore((state) => state.setIsSearchDataLoading);

  // 初始化轻量搜索数据（热词关键词列表，不含商品）
  const initSearchKeywords = async () => {
    try {
      // 1. 获取搜索自动补全数据（轻量）
      const keywordsResponse = await getSearchKeywords();
      if (keywordsResponse && keywordsResponse.code === 200) {
        const newKeywordsMaps: any[] = [];

        keywordsResponse.data.attributes.forEach((item: string) => {
          newKeywordsMaps.push({
            is_attr: '1',
            key: item.toLowerCase(),
            label: item,
          });
        });

        keywordsResponse.data.products.forEach((item: any) => {
          newKeywordsMaps.push({
            is_product: '1',
            key: item.name.toLowerCase(),
            value: item.shop_sku,
            label: item.name,
          });
        });

        setKeywordsMaps(newKeywordsMaps);
      }

      // 2. 获取魔方配置搜索词数据（轻量，只有关键词）
      const homePageResponse = await getHomePageData({ block_type: 9, status: 1 });
      if (homePageResponse.code === 200) {
        const { data } = homePageResponse;
        setSearchData(homePageResponse.data.list[0]);

        // 3. 初始化热词列表（只有关键词，不含商品数据）
        if (data.list[0] && (data.list[0] as any).hotKeywords?.length) {
          const hotList = (data.list[0] as any).hotKeywords.map((item: any) => ({
            ...item,
            keywords: item.name,
            products: [], // 商品数据延迟加载
          }));
          setHotProductList(hotList);
        }
      }
    } catch (error) {
      logger.error('Failed to initialize search keywords:', error);
    }
  };

  // 加载商品数据（重量数据，按需加载）
  const loadSearchProducts = async () => {
    try {
      const { searchData } = useSearchStore.getState();
      if (!searchData || !(searchData as any).hotKeywords?.length) {
        return;
      }

      // 获取热搜词带商品的列表
      const hotNames = (searchData as any).hotKeywords.map((item: any) => item.name);
      const hotResponse = await getSearchDataByType('hot', { keywords: hotNames, limit: 6 });

      if (hotResponse.data.length) {
        const hotList = (searchData as any).hotKeywords.map((item: any) => {
          const productData = hotResponse.data.find((i: any) => item.name === i.keywords);
          return {
            ...item,
            keywords: item.name,
            ...productData,
            products: productData ? productData.products : [],
          };
        });
        setHotProductList(hotList);
      }
    } catch (error) {
      logger.error('Failed to load search products:', error);
    }
  };

  // 按需加载商品数据（重量数据）
  const loadSearchDataIfNeeded = useCallback(async () => {
    // 如果已加载或正在加载，直接返回
    if (isSearchDataLoaded || isSearchDataLoading) {
      return;
    }

    try {
      setIsSearchDataLoading(true);
      await loadSearchProducts(); // 只加载商品数据
      setIsSearchDataLoaded(true);
    } catch (error) {
      logger.error('Failed to load search data:', error);
    } finally {
      setIsSearchDataLoading(false);
    }
  }, [isSearchDataLoaded, isSearchDataLoading, setIsSearchDataLoaded, setIsSearchDataLoading]);

  useEffect(() => {
    isClientInit.current = true;
    const searchHistoryCache = localStorage.getItem('zl-searchHistory');
    if (searchHistoryCache) {
      setSearchHistory(JSON.parse(searchHistoryCache) as string[]);
    }
    // 初始化轻量数据（热词关键词），商品数据延迟加载
    initSearchKeywords();
  }, []);

  useEffect(() => {
    const qtyArr: number[] = [];
    if (canBuyCartsList && canBuyCartsList.length) {
      canBuyCartsList.forEach((item) => {
        (item as { carts: Array<{ qty: number }> }).carts.forEach((items) => {
          qtyArr.push(items.qty);
        });
      });
    }
    let sum: number | string = 0;
    qtyArr.forEach((i) => {
      (sum as number) += i;
    });
    if (sum > 99) {
      sum = '...';
    }
    setCartProductNum(sum);
  }, [canBuyCartsList]);

  useEffect(() => {
    const {
      isShowHeaderNav,
      isHeaderDarkMode,
      isShowDtBanner,
      isShowAppDownloadBanner: issp,
    } = headerConfig;

    setIsShowHeaderNav(isShowHeaderNav);
    setIsHeaderDarkMode(isHeaderDarkMode);
    setIsShowDtBannerByPage(isShowDtBanner);
    setIsShowAppDownloadBanner(issp);
  }, [pathname, isMobileDevice]);
  const finalHeaderConfig = useMemo(() => {
    if (isClientInit.current) {
      return {
        isShowHeader: isShowHeader,
        isDarkMode,
        isShowDtBannerByPage,
        isShowAppDownloadBanner,
      };
    }
    return {
      isShowHeader: headerConfig.isShowHeaderNav,
      isDarkMode: headerConfig.isHeaderDarkMode,
      isShowDtBannerByPage: headerConfig.isShowDtBanner,
      isShowAppDownloadBanner: headerConfig.isShowAppDownloadBanner,
    };
  }, [isShowHeader, isDarkMode, isShowDtBannerByPage, isShowAppDownloadBanner, headerConfig]);

  const finalProps = Object.assign({}, props, finalHeaderConfig);
  return (
    <>
      {props.deviceType === EPlatform.WAP && (
        <HeaderMobile
          {...finalProps}
          cartProductNum={cartProductNum}
          deviceType={props.deviceType}
          loadSearchDataIfNeeded={loadSearchDataIfNeeded}
          isBot={props.isBot}
        />
      )}
      {props.deviceType === EPlatform.WEB && (
        <HeaderPC
          {...finalProps}
          cartProductNum={cartProductNum}
          loadSearchDataIfNeeded={loadSearchDataIfNeeded}
          isBot={props.isBot}
        />
      )}
    </>
  );
};

export default ZeeloolHeader;
